import React, { Fragment, useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import MemeContent from './MemeContent';
import MemeReply from './MemeReply';
import { CommentType, PostType } from '../memeverse_types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import LoadingPage from '../../LoadingPage';
import {
  getCachedCommentLimit,
  setCachedCommentLimit,
  getMaxObservedCommentHeight,
  updateMaxObservedCommentHeight,
  shouldPrerender,
} from './commentLimitCacheManager';
import { useDocumentRefreshState } from './RefreshStatesContext';
interface MemeProps {
  post: PostType;
  allComments?: Record<string, CommentType>;
  onReplyClick: (item: PostType | CommentType) => void;
  isNewsFeed?: boolean;
}

const Meme: React.FC<MemeProps> = ({ post, allComments, onReplyClick, isNewsFeed = false }) => {
  const memeContentRef = useRef<HTMLDivElement>(null);
  const memeWrapperRef = useRef<HTMLDivElement>(null);
  const showCommentsRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [typingCompleted, setTypingCompleted] = useState<Set<string>>(new Set());
  const initialCachedLimit = getCachedCommentLimit(post.docId, window.innerWidth, window.innerHeight);
  const [dynamicCommentLimit, setDynamicCommentLimit] = useState<number>(initialCachedLimit?.commentLimit || 0);
  const [lastValidCommentLimit, setLastValidCommentLimit] = useState<number>(initialCachedLimit?.commentLimit || 0);
  const [availableImageHeight, setAvailableImageHeight] = useState<number | undefined>(initialCachedLimit?.imageHeight || undefined);
  const [isPrerendering, setIsPrerendering] = useState<boolean>(() => {
    // Only prerender if we don't have a cached limit for this post
    return shouldPrerender(post.docId, window.innerWidth, window.innerHeight);
  });

  // Track if we've ever had a valid calculation to prevent resetting to prerender mode
  const [hasCalculatedOnce, setHasCalculatedOnce] = useState<boolean>(() => {
    return !!initialCachedLimit;
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const refreshState = useDocumentRefreshState(post.docId);

  const calcCommentLimit: () => { commentLimit: number; imageHeight: number | undefined } =
    useCallback(() => {
      if (!post?.flat_comments_list?.length || !allComments) {
        return { commentLimit: 0, imageHeight: undefined };
      }

      // Check if we already have a cached limit for this post
      const commentCount = post.flat_comments_list.length;
      const cached = getCachedCommentLimit(post.docId, window.innerWidth, window.innerHeight);

      // If we have a cached value, check if we need to recalculate
      if (cached) {
        const commentDiff = commentCount - cached.commentCount;

        // If comment count hasn't changed, use cached value
        if (commentDiff === 0) {
          return { commentLimit: cached.commentLimit, imageHeight: cached.imageHeight };
        }

        // If we had 0 limit cached and now have new comments, always recalculate
        if (cached.commentLimit === 0 && commentCount > 0) {
          // Continue to recalculation below
        }
        // If we have exactly one new comment and space for it, try to fit it
        else if (commentDiff === 1 && cached.commentLimit > 0) {
          // Continue to recalculation below
        }
        // For other cases where comment count changed significantly, use cached as baseline
        else if (Math.abs(commentDiff) > 1) {
          return { commentLimit: cached.commentLimit, imageHeight: cached.imageHeight };
        }
      }

      // Get container dimensions
      const defaultHeight = window.innerHeight - (isXs ? 225 : 117);
      const containerHeight = (memeWrapperRef?.current?.offsetHeight || defaultHeight) - 10;

      // Get "show more comments" button height
      const moreCommentsButtonHeight = showCommentsRef?.current?.offsetHeight || 24;

      // Check if post has images that could be shrunk
      const hasImages = post && post.images && post.images.filter((val) => !!val).length > 0;

      // Calculate base post height (text content only, excluding images)
      let basePostHeight = memeContentRef?.current?.offsetHeight || (isMobile ? 170 : 157);

      // If we have images, subtract the current image height to get just text content height
      if (hasImages && imageContainerRef?.current?.offsetHeight) {
        basePostHeight -= imageContainerRef.current.offsetHeight;
      }

      // Add minimum image height for calculation
      const minImageHeight = isMobile ? 150 : 200;
      if (hasImages) {
        basePostHeight += minImageHeight;
      }

      // Calculate available space for comments
      const availableHeight = containerHeight - basePostHeight - moreCommentsButtonHeight;

      if (availableHeight <= 0) {
        return { commentLimit: 0, imageHeight: undefined };
      }

      // Dynamic fallback height based on maximum observed height, with device-specific defaults
      const deviceDefaultHeight = isMobile ? 170 : 157;
      const maxHeight = getMaxObservedCommentHeight();
      const fallbackCommentHeight = maxHeight > 0 ? maxHeight : deviceDefaultHeight;

      let totalCommentsHeight = 0;
      let commentsToShow = 0;

      // Iterate through comments from last to first (most recent first)
      for (let i = post.flat_comments_list.length - 1; i >= 0; i--) {
        const commentId = post.flat_comments_list[i];
        const comment = allComments[commentId];

        if (!comment) continue;

        // Try to get actual height of rendered comment if available
        const commentElement = document.querySelector(`[data-comment-id="${commentId}"]`) as HTMLElement;
        const commentHeight = commentElement?.offsetHeight || fallbackCommentHeight;

        // Update maximum observed height for better future estimates
        if (commentElement?.offsetHeight) {
          updateMaxObservedCommentHeight(commentElement.offsetHeight);
        }

        // Check if adding this comment would exceed available space
        if (totalCommentsHeight + commentHeight > availableHeight) {
          break;
        }

        totalCommentsHeight += commentHeight;
        commentsToShow++;
      }

      const finalLimit = Math.max(0, commentsToShow);

      // Calculate available image height
      let imageHeight: number | undefined;

      if (hasImages) {
        // Calculate text-only height (excluding current image height if any)
        let textOnlyHeight = memeContentRef?.current?.offsetHeight || (isMobile ? 170 : 157);
        if (imageContainerRef?.current?.offsetHeight) {
          textOnlyHeight -= imageContainerRef.current.offsetHeight;
        }

        const remainingHeight = containerHeight - totalCommentsHeight - moreCommentsButtonHeight - textOnlyHeight;
        imageHeight = remainingHeight;
      }

      const result = { commentLimit: finalLimit, imageHeight };

      // Cache the result
      setCachedCommentLimit(post.docId, commentCount, window.innerWidth, window.innerHeight, result);

      return result;
  }, [post, post?.flat_comments_list?.length, allComments, isMobile, isXs]); // Need this dependency to detect comment changes

  // Use dynamic comment limit, fallback to static estimate for initial render
  const commentLimit: number = useMemo(() => {
    // If we don't have any comments, return 0
    if (!post?.flat_comments_list?.length) {
      return 0;
    }

    // During prerendering, render ALL comments (they'll be invisible anyway)
    if (isPrerendering && !hasCalculatedOnce) {
      return post.flat_comments_list.length;
    }

    let limit = 0;

    if (dynamicCommentLimit > 0) {
      limit = dynamicCommentLimit;
    } else if (initialCachedLimit && !hasCalculatedOnce) {
      // Use cached limit if available and we haven't calculated yet
      limit = initialCachedLimit.commentLimit;
    } else {
      const calculatedLimit = calcCommentLimit().commentLimit;
      limit = calculatedLimit;
    }

    // Use the last valid limit as fallback to prevent momentary 0 flicker
    // Only update lastValidCommentLimit if we get a positive calculated limit
    if (limit > 0) {
      setLastValidCommentLimit(limit);
      if (!hasCalculatedOnce) {
        setHasCalculatedOnce(true);
      }
      return limit;
    } else {
      // Return last valid limit
      return lastValidCommentLimit > 0 ? lastValidCommentLimit : Math.min(0, post.flat_comments_list.length);
    }
  }, [dynamicCommentLimit, isPrerendering, post?.flat_comments_list?.length,
      lastValidCommentLimit, initialCachedLimit, hasCalculatedOnce]);

  const mainMeme = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Effect to handle prerendering and calculate final comment limit
  useEffect(() => {
    if (!isPrerendering || hasCalculatedOnce) return;

    const finalizeLimitCalculation = () => {
      // Only calculate if DOM elements are ready
      if (memeWrapperRef.current) {
        const result = calcCommentLimit();
        setDynamicCommentLimit(result.commentLimit);
        setAvailableImageHeight(result.imageHeight);
        setIsPrerendering(false);
        setHasCalculatedOnce(true);
      } else {
        // Retry if DOM not ready yet
        setTimeout(finalizeLimitCalculation, 100);
      }
    };

    // Allow time for prerendered elements to be measured
    const timeoutId = setTimeout(finalizeLimitCalculation, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [post, allComments, isPrerendering, hasCalculatedOnce]);

  // Effect to recalculate on window resize (after initial render)
  useEffect(() => {
    if (isPrerendering || !hasCalculatedOnce) return;

    const handleResize = () => {
      const result = calcCommentLimit();
      if (result.commentLimit !== dynamicCommentLimit) {
        setDynamicCommentLimit(result.commentLimit);
        setAvailableImageHeight(result.imageHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dynamicCommentLimit, isPrerendering, hasCalculatedOnce]);

  // Effect to recalculate comment limit when comments change
  useEffect(() => {
    if (isPrerendering || !hasCalculatedOnce) return;

    const result = calcCommentLimit();
    if (result.commentLimit !== dynamicCommentLimit) {
      setDynamicCommentLimit(result.commentLimit);
      setAvailableImageHeight(result.imageHeight);
    }
  }, [post.flat_comments_list?.length, isPrerendering, hasCalculatedOnce]);

  const ReplyComponent = ({
    commentId,
    comment,
    show_typing,
    isHidden = false,
    disableTyping = false,
    onTypingComplete,
  }: {
    commentId: string;
    comment: CommentType;
    show_typing: boolean;
    isHidden?: boolean;
    disableTyping?: boolean;
    onTypingComplete?: (commentId: string) => void;
  }) => {
    // const replyWithTyping = useMemo(() => ({
    //   ...comment,
    //   show_typing: disableTyping ? false : show_typing
    // }), [comment, show_typing, disableTyping]);

    return (
      <div
        data-comment-id={commentId}
        style={isHidden ? {
          visibility: 'hidden',
          position: 'absolute',
          left: 0,
          right: 0,
          pointerEvents: 'none'
        } : {
          width: '100%',
        }}
      >
        <MemeReply
          key={`comment-${commentId}`}
          parentPostOrComment={post}
          docId={commentId}
          showTyping={show_typing}
          // reply={replyWithTyping}
          showNested={false}
          shouldLimitLength={true}
          allComments={allComments}
          onReplyClick={onReplyClick}
          onTypingComplete={onTypingComplete}
        />
      </div>
    );
  };

  // Callback to handle typing completion
  const handleTypingComplete = (commentId: string) => {
    setTypingCompleted(prev => new Set(prev).add(commentId));
  };

  const RenderedComments: React.FC = () => {
    if (!post?.flat_comments_list?.length) return null;

    // During prerendering, render all comments hidden for measurement
    if (isPrerendering && !hasCalculatedOnce) {
      return (
        <>
          {
            post.flat_comments_list.map((commentId) => {
              const comment = allComments[commentId];
              if (!comment) return null;

              return (
                <ReplyComponent
                  key={`comment-${commentId}-prerender}`}
                  commentId={commentId}
                  comment={comment}
                  show_typing={false}
                  isHidden={true}
                  disableTyping={true}
                  onTypingComplete={handleTypingComplete}
                />
              );
            }).filter(Boolean)
          }
        </>
      )
    }

    // After prerendering, show comments within the limit, with thinking animations not counting against limit when space available
    const regularComments = post.flat_comments_list.filter(commentId => {
      const comment = allComments[commentId];
      return comment && !comment.isThinking && !commentId.startsWith('thinking-');
    });

    const isAtPhysicalLimit = commentLimit < regularComments.length;

    return commentLimit > 0 &&
    <> {
      post.flat_comments_list.map((commentId, index) => {
        const comment = allComments[commentId];
        const isLastComment = index === post.flat_comments_list.length - 1;
        const isThinking = comment?.isThinking || commentId.startsWith('thinking-');

        // Show typing animation for the last comment if it hasn't completed yet
        const shouldShowTyping = isLastComment && !typingCompleted.has(commentId);

        let shouldShowComment = false;

        if (isThinking) {
          // Always show thinking animations unless we're at physical limit
          shouldShowComment = !!comment && !isAtPhysicalLimit;
        } else {
          // For regular comments, show based on the limit
          if (!isAtPhysicalLimit) {
            // If we're not at physical limit, show all regular comments
            shouldShowComment = !!comment;
          } else {
            // If we're at physical limit, show only the most recent comments within the limit
            const regularCommentIndex = regularComments.indexOf(commentId);
            shouldShowComment = !!comment && regularCommentIndex >= regularComments.length - commentLimit;
          }
        }

        return shouldShowComment ? (
          <ReplyComponent
            key={`comment-${commentId}}`}
            commentId={commentId}
            comment={comment}
            show_typing={shouldShowTyping}
            isHidden={false}
            disableTyping={false}
            onTypingComplete={handleTypingComplete}
          />
        ) : (
          // skip comment if not in allComments or beyond comment limit
          <Box
            key={`comment-${commentId}`}
            className={
              !comment
                ? `not-found ${commentId}`
                : `comment-limit-skipped ${commentId}`
            }
          >
            <Fragment key={`comment-${commentId}`} />
          </Box>
        );
      })
    }</>
  };

  if (!post) {
    // skip post if not found
    return <></>;
  }

  return (
    <Box
      ref={mainMeme}
      className='meme'
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        maxWidth: '100dvw',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 1.5, md: 0 },
        position: 'relative', // For loading overlay positioning
      }}
    >
      {/* Loading overlay during prerendering */}
      {isPrerendering && !hasCalculatedOnce && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'background.paper',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingPage />
        </Box>
      )}
      <Box
        className='meme-wrapper'
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
          maxWidth: '100dvw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // Vertically center children
          alignItems: 'center',
          position: 'relative', // Make this the relative positioning context
          visibility: (isPrerendering && !hasCalculatedOnce) ? 'hidden' : 'visible',
          pointerEvents: (isPrerendering && !hasCalculatedOnce) ? 'none' : 'auto',
        }}
        ref={memeWrapperRef}
      >
        <Box
          ref={memeContentRef}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: (!isPrerendering && post.images?.length) ? '1 1 auto' : 'none',
            minHeight: 0,
            maxHeight: 'fit-content',
          }}
        >
          <MemeContent
            post={post}
            availableImageHeight={availableImageHeight}
            onReplyClick={() => onReplyClick(post)}
            showImage={!(isPrerendering && !hasCalculatedOnce)}
            imageContainerRef={imageContainerRef}
            isNewsFeed={isNewsFeed}
          />
        </Box>
        <RenderedComments />
        {!!post?.comments_counts && post.comments_counts > commentLimit && (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              pt: 1,
            }}
            ref={showCommentsRef}
          >
            <Button
              variant='outlined'
              color='secondary'
              component='a'
              href={`/ask/post/${post.docId}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/ask/post/${post.docId}`, {
                  state: {
                    from: location.pathname,
                    scrollY:
                      document.getElementById('ask-scroll-box')?.scrollTop || 0,
                  },
                });
              }}
            >
              {`See all ${post.comments_counts} comments`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Meme;
